import {
  useMemo,
  useState,
} from "react";

import {
  supabase,
} from "../../lib/supabaseClient";

import {
  usePrpSessionState,
} from "../../lib/prpUiContinuity";

// LOT0F13_PRP_DEMAND_RADAR_GAP_MAP_V1
//
// Demand Radar is an assistive opportunity signal built from:
// - real Google Maps Places results returned by the existing region-locked
//   prp-nearby-search Edge;
// - the PRP's current CRM leads and the provenance already recorded in
//   prp_activities.
// It is NOT a market-share census and never promotes Google coordinates to
// official Forfeo geography.

type AnyRecord = Record<string, any>;

type Props = {
  leads: AnyRecord[];
  activities: AnyRecord[];
  onRefresh: () => Promise<void> | void;
};

type NearbyPlace = {
  provider_place_id: string;
  name: string;
  address: string;
  primary_type: string;
  primary_type_label: string;
  latitude: number | null;
  longitude: number | null;
  distance_m: number | null;
  source: string;
  verification_state: string;
  territory_region?: string | null;
};

type RadarScan = {
  region: string;
  radius_km: number;
  provider_strategy: string;
  provider_search_count: number;
  outside_territory_filtered_count: number;
  places: NearbyPlace[];
};

type SaveState =
  | "idle"
  | "saving"
  | "saved"
  | "duplicate"
  | "error";

type ForfyState = {
  loading: boolean;
  saving: boolean;
  saved: boolean;
  payload?: AnyRecord;
  error?: string;
};

type VerticalKey =
  | "restaurants"
  | "spas"
  | "hotels"
  | "cafes"
  | "boutiques"
  | "activities";

type VerticalDefinition = {
  key: VerticalKey;
  label: string;
  emoji: string;
  types: string[];
};

const VERTICALS: VerticalDefinition[] = [
  {
    key: "restaurants",
    label: "Restaurants",
    emoji: "🍽️",
    types: [
      "restaurant",
    ],
  },
  {
    key: "spas",
    label: "Spas & bien-être",
    emoji: "🧖",
    types: [
      "spa",
      "massage_spa",
      "wellness_center",
    ],
  },
  {
    key: "hotels",
    label: "Hôtels",
    emoji: "🏨",
    types: [
      "hotel",
      "resort_hotel",
      "bed_and_breakfast",
      "motel",
    ],
  },
  {
    key: "cafes",
    label: "Cafés & boulangeries",
    emoji: "☕",
    types: [
      "cafe",
      "coffee_shop",
      "bakery",
    ],
  },
  {
    key: "boutiques",
    label: "Boutiques",
    emoji: "🛍️",
    types: [
      "store",
      "gift_shop",
      "clothing_store",
      "book_store",
      "jewelry_store",
    ],
  },
  {
    key: "activities",
    label: "Activités",
    emoji: "🎟️",
    types: [
      "tourist_attraction",
      "museum",
      "amusement_park",
      "aquarium",
      "zoo",
      "adventure_sports_center",
    ],
  },
];

const ALL_CATEGORY_KEYS =
  VERTICALS.map(
    (vertical) => vertical.key,
  );

const text = (
  value: unknown,
  fallback = "",
): string =>
  typeof value === "string"
  && value.trim()
    ? value.trim()
    : fallback;

const numberValue = (
  value: unknown,
): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? parsed
    : null;
};

const stageOf = (
  lead: AnyRecord,
): string =>
  text(
    lead.pipeline_stage
    || lead.status,
    "new",
  ).toLowerCase();

const metadataOf = (
  activity: AnyRecord,
): AnyRecord =>
  activity.metadata
  && typeof activity.metadata === "object"
  && !Array.isArray(activity.metadata)
    ? activity.metadata as AnyRecord
    : {};

const verticalForType = (
  primaryType: unknown,
  category: unknown = "",
): VerticalKey | null => {
  const normalizedType =
    text(primaryType)
      .toLowerCase();

  const normalizedCategory =
    text(category)
      .toLowerCase();

  for (
    const vertical
    of VERTICALS
  ) {
    if (
      vertical.types.includes(
        normalizedType,
      )
    ) {
      return vertical.key;
    }
  }

  const combined =
    `${normalizedType} ${normalizedCategory}`;

  if (/restaurant/.test(combined)) return "restaurants";
  if (/spa|massage|wellness|bien-être|bien etre/.test(combined)) return "spas";
  if (/hotel|hôtel|resort|motel|bed.and.breakfast/.test(combined)) return "hotels";
  if (/cafe|café|coffee|bakery|boulanger/.test(combined)) return "cafes";
  if (/museum|musée|attraction|aquarium|zoo|adventure|activité|activite/.test(combined)) return "activities";
  if (/store|shop|boutique|clothing|book|jewelry|gift/.test(combined)) return "boutiques";

  return null;
};

const formatDistance = (
  meters: number | null,
): string => {
  if (
    meters === null
    || !Number.isFinite(
      meters,
    )
  ) {
    return "Distance inconnue";
  }

  if (meters < 1000) {
    return `${Math.max(
      1,
      Math.round(meters),
    )} m`;
  }

  return `${(
    meters / 1000
  ).toFixed(
    meters < 3000
      ? 1
      : 0,
  )} km`;
};

const mapsUrl = (
  place: NearbyPlace,
): string => {
  const query =
    place.latitude !== null
    && place.longitude !== null
      ? `${place.latitude},${place.longitude}`
      : place.name;

  const params =
    new URLSearchParams({
      api: "1",
      query,
    });

  if (
    place.provider_place_id
  ) {
    params.set(
      "query_place_id",
      place.provider_place_id,
    );
  }

  return `https://www.google.com/maps/search/?${params.toString()}`;
};

const sha256Hex = async (
  value: string,
): Promise<string> => {
  const digest =
    await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder()
        .encode(value),
    );

  return Array.from(
    new Uint8Array(
      digest,
    ),
  )
    .map(
      (byte) =>
        byte
          .toString(16)
          .padStart(2, "0"),
    )
    .join("");
};

const candidateIdempotencyKey = async (
  providerPlaceId: string,
): Promise<string> => {
  const digest =
    await sha256Hex(
      providerPlaceId,
    );

  return `gap-radar:${digest.slice(0, 48)}`;
};

const randomIdempotencyKey = (
  prefix: string,
): string => {
  const suffix =
    typeof crypto !== "undefined"
    && typeof crypto.randomUUID
      === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `${prefix}:${suffix}`
    .slice(0, 100);
};

const invokePrpEdge = async (
  functionName: string,
  body: AnyRecord,
): Promise<AnyRecord> => {
  const {
    data: {
      session,
    },
    error:
      sessionError,
  } =
    await supabase.auth
      .getSession();

  if (sessionError) {
    throw sessionError;
  }

  const token =
    session?.access_token
      ?.trim();

  if (!token) {
    throw new Error(
      "prp_session_required",
    );
  }

  const result =
    await supabase.functions
      .invoke(
        functionName,
        {
          body,
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        },
      );

  if (!result.error) {
    return (
      result.data
      || {}
    ) as AnyRecord;
  }

  const errorData =
    (
      result.data
      || {}
    ) as AnyRecord;

  let code =
    typeof errorData.error
    === "string"
      ? String(
          errorData.error,
        )
      : "";

  const context =
    (result.error as any)
      ?.context;

  const response =
    typeof Response
    !== "undefined"
    && context
      instanceof Response
      ? context
      : typeof Response
        !== "undefined"
        && context?.response
          instanceof Response
        ? context.response
        : null;

  if (
    !code
    && response
  ) {
    try {
      const payload =
        await response
          .clone()
          .json();

      if (
        typeof payload?.error
        === "string"
      ) {
        code =
          payload.error;
      }
    } catch {
      // Generic fail-closed error below.
    }
  }

  throw new Error(
    code
    || result.error.message
    || "prp_edge_failed",
  );
};

const currentPosition = (): Promise<{
  latitude: number;
  longitude: number;
}> =>
  new Promise(
    (
      resolve,
      reject,
    ) => {
      if (
        !("geolocation" in navigator)
      ) {
        reject(
          new Error(
            "geolocation_unavailable",
          ),
        );
        return;
      }

      navigator.geolocation
        .getCurrentPosition(
          (result) => {
            resolve({
              latitude:
                result.coords.latitude,
              longitude:
                result.coords.longitude,
            });
          },
          (error) => {
            if (
              error.code
              === error.PERMISSION_DENIED
            ) {
              reject(
                new Error(
                  "geolocation_permission_denied",
                ),
              );
              return;
            }

            if (
              error.code
              === error.TIMEOUT
            ) {
              reject(
                new Error(
                  "geolocation_timeout",
                ),
              );
              return;
            }

            reject(
              new Error(
                "geolocation_failed",
              ),
            );
          },
          {
            enableHighAccuracy:
              false,
            timeout:
              12000,
            maximumAge:
              60000,
          },
        );
    },
  );

export default function PrpDemandRadarGapMap({
  leads,
  activities,
  onRefresh,
}: Props) {
  const [consent, setConsent] =
    useState(false);

  const [radiusKm, setRadiusKm] =
    usePrpSessionState<number>(
      "demand-radar:radius-km",
      25,
      (value) =>
        typeof value === "number"
        && Number.isFinite(value)
        && value >= 1
        && value <= 100,
    );

  const [scan, setScan] =
    useState<RadarScan | null>(
      null,
    );

  const [scanning, setScanning] =
    useState(false);

  const [error, setError] =
    useState("");

  const [saveStates, setSaveStates] =
    useState<Record<string, SaveState>>(
      {},
    );

  const [snapshotSaving, setSnapshotSaving] =
    useState(false);

  const [snapshotSaved, setSnapshotSaved] =
    useState(false);

  const [forfy, setForfy] =
    useState<ForfyState | null>(
      null,
    );

  const provenanceByLead =
    useMemo(
      () => {
        const map =
          new Map<string, AnyRecord>();

        for (
          const activity
          of activities
        ) {
          const metadata =
            metadataOf(
              activity,
            );

          const leadId =
            text(
              activity.lead_id,
            );

          if (
            !leadId
            || metadata.event
              !== "nearby_prospect_saved"
          ) {
            continue;
          }

          if (
            !map.has(
              leadId,
            )
          ) {
            map.set(
              leadId,
              metadata,
            );
          }
        }

        return map;
      },
      [
        activities,
      ],
    );

  const existingProviderIds =
    useMemo(
      () => {
        const ids =
          new Set<string>();

        for (
          const activity
          of activities
        ) {
          const metadata =
            metadataOf(
              activity,
            );

          if (
            metadata.event
            !== "nearby_prospect_saved"
          ) {
            continue;
          }

          const providerId =
            text(
              metadata.provider_place_id,
            );

          if (providerId) {
            ids.add(
              providerId,
            );
          }
        }

        return ids;
      },
      [
        activities,
      ],
    );

  const radarRows =
    useMemo(
      () => {
        if (!scan) {
          return [];
        }

        return VERTICALS
          .map(
            (vertical) => {
              const localPlaces =
                scan.places
                  .filter(
                    (place) =>
                      verticalForType(
                        place.primary_type,
                        place.primary_type_label,
                      )
                      === vertical.key,
                  );

              const untrackedPlaces =
                localPlaces
                  .filter(
                    (place) =>
                      !existingProviderIds
                        .has(
                          place.provider_place_id,
                        ),
                  );

              const linkedLeads =
                leads.filter(
                  (lead) => {
                    const leadId =
                      text(
                        lead.id,
                      );

                    const metadata =
                      provenanceByLead
                        .get(
                          leadId,
                        );

                    if (!metadata) {
                      return false;
                    }

                    return verticalForType(
                      metadata.source_primary_type,
                      metadata.source_category,
                    ) === vertical.key;
                  },
                );

              const activePartners =
                linkedLeads.filter(
                  (lead) =>
                    stageOf(lead)
                    === "active",
                ).length;

              const approvedPartners =
                linkedLeads.filter(
                  (lead) =>
                    stageOf(lead)
                    === "approved",
                ).length;

              const pipelineCount =
                linkedLeads.filter(
                  (lead) =>
                    ![
                      "lost",
                      "active",
                    ].includes(
                      stageOf(lead),
                    ),
                ).length;

              const supplySignal =
                Math.min(
                  50,
                  untrackedPlaces.length
                  * 5,
                );

              const coverageGap =
                activePartners
                === 0
                  ? 30
                  : Math.max(
                      0,
                      20
                      - activePartners
                        * 5,
                    );

              const pipelineGap =
                pipelineCount
                === 0
                  ? 20
                  : Math.max(
                      0,
                      10
                      - pipelineCount
                        * 2,
                    );

              const priorityScore =
                Math.min(
                  100,
                  Math.round(
                    supplySignal
                    + coverageGap
                    + pipelineGap,
                  ),
                );

              return {
                ...vertical,
                local_places:
                  localPlaces.length,
                untracked_places:
                  untrackedPlaces.length,
                active_partners:
                  activePartners,
                approved_partners:
                  approvedPartners,
                pipeline_count:
                  pipelineCount,
                priority_score:
                  priorityScore,
                candidates:
                  untrackedPlaces
                    .slice(0, 3),
              };
            },
          )
          .sort(
            (left, right) =>
              right.priority_score
              - left.priority_score,
          );
      },
      [
        existingProviderIds,
        leads,
        provenanceByLead,
        scan,
      ],
    );

  const scanTerritory =
    async () => {
      if (!consent) {
        setError(
          "Confirme d’abord l’autorisation temporaire de localisation.",
        );
        return;
      }

      setScanning(
        true,
      );
      setError("");
      setScan(
        null,
      );
      setForfy(
        null,
      );
      setSnapshotSaved(
        false,
      );

      try {
        const position =
          await currentPosition();

        const payload =
          await invokePrpEdge(
            "prp-nearby-search",
            {
              action:
                "search_nearby",
              center: {
                latitude:
                  position.latitude,
                longitude:
                  position.longitude,
              },
              radius_m:
                Math.round(
                  radiusKm
                  * 1000,
                ),
              categories:
                ALL_CATEGORY_KEYS,
            },
          );

        if (
          payload.ok
          !== true
        ) {
          throw new Error(
            text(
              payload.error,
              "demand_radar_search_failed",
            ),
          );
        }

        const places =
          Array.isArray(
            payload.results,
          )
            ? payload.results
                .filter(
                  (place) =>
                    place
                    && typeof place
                      === "object",
                )
                .map(
                  (place) => ({
                    provider_place_id:
                      text(
                        place.provider_place_id,
                      ),
                    name:
                      text(
                        place.name,
                        "Commerce",
                      ),
                    address:
                      text(
                        place.address,
                      ),
                    primary_type:
                      text(
                        place.primary_type,
                        "local_business",
                      ),
                    primary_type_label:
                      text(
                        place.primary_type_label,
                        "Commerce local",
                      ),
                    latitude:
                      numberValue(
                        place.latitude,
                      ),
                    longitude:
                      numberValue(
                        place.longitude,
                      ),
                    distance_m:
                      numberValue(
                        place.distance_m,
                      ),
                    source:
                      text(
                        place.source,
                        "google_maps_places",
                      ),
                    verification_state:
                      text(
                        place.verification_state,
                        "temporary_unverified",
                      ),
                    territory_region:
                      text(
                        place.territory_region,
                      )
                      || null,
                  }))
                .filter(
                  (place) =>
                    Boolean(
                      place.provider_place_id,
                    ),
                )
            : [];

        setScan({
          region:
            text(
              payload.territory_region,
              "Région PRP",
            ),
          radius_km:
            Number(
              payload.requested_radius_km
              || radiusKm,
            ),
          provider_strategy:
            text(
              payload.provider_strategy,
              "region_locked",
            ),
          provider_search_count:
            Number(
              payload.provider_search_count
              || 1,
            ),
          outside_territory_filtered_count:
            Number(
              payload.outside_territory_filtered_count
              || 0,
            ),
          places,
        });
      } catch (caught) {
        const code =
          caught
          instanceof Error
            ? caught.message
            : "demand_radar_failed";

        const friendly =
          code
          === "position_outside_assigned_region"
            ? "Ta position actuelle est hors de ta région PRP assignée."
            : code
              === "geolocation_permission_denied"
              ? "Permission de localisation refusée."
              : code
                === "geolocation_timeout"
                ? "La localisation a expiré. Réessaie."
                : code
                  === "territory_boundary_service_unavailable"
                  ? "Le service officiel des limites régionales est temporairement indisponible."
                  : code;

        setError(
          friendly,
        );
      } finally {
        setScanning(
          false,
        );
      }
    };

  const saveCandidate =
    async (
      place: NearbyPlace,
    ) => {
      const providerId =
        place.provider_place_id;

      if (
        !providerId
        || saveStates[providerId]
          === "saving"
        || saveStates[providerId]
          === "saved"
        || saveStates[providerId]
          === "duplicate"
      ) {
        return;
      }

      setSaveStates(
        (current) => ({
          ...current,
          [providerId]:
            "saving",
        }),
      );

      try {
        const idempotencyKey =
          await candidateIdempotencyKey(
            providerId,
          );

        const payload =
          await invokePrpEdge(
            "prp-workspace-command",
            {
              action:
                "nearby_lead_save",
              idempotency_key:
                idempotencyKey,
              source:
                "google_maps_places",
              provider_place_id:
                providerId,
              verification_state:
                "temporary_unverified",
              business_name:
                place.name,
              address:
                place.address,
              category:
                place.primary_type_label,
              primary_type:
                place.primary_type,
              source_latitude:
                place.latitude,
              source_longitude:
                place.longitude,
              distance_m:
                place.distance_m,
            },
          );

        const result =
          payload.result
          && typeof payload.result
            === "object"
          && !Array.isArray(
            payload.result,
          )
            ? (payload.result as AnyRecord)
            : {};

        const duplicate =
          payload.duplicate
          === true
          || result.duplicate
            === true;

        setSaveStates(
          (current) => ({
            ...current,
            [providerId]:
              duplicate
                ? "duplicate"
                : "saved",
          }),
        );

        if (!duplicate) {
          await Promise.resolve(
            onRefresh(),
          );
        }
      } catch {
        setSaveStates(
          (current) => ({
            ...current,
            [providerId]:
              "error",
          }),
        );
      }
    };

  const saveSnapshot =
    async () => {
      if (
        !scan
        || radarRows.length
          === 0
        || snapshotSaving
        || snapshotSaved
      ) {
        return;
      }

      setSnapshotSaving(
        true,
      );

      try {
        const top =
          radarRows
            .slice(0, 3)
            .map(
              (row) =>
                `${row.label} ${row.priority_score}/100`,
            )
            .join(" · ");

        await invokePrpEdge(
          "prp-workspace-command",
          {
            action:
              "activity_add",
            idempotency_key:
              randomIdempotencyKey(
                "demand-radar-snapshot",
              ),
            activity_type:
              "note",
            summary:
              `Demand Radar — ${scan.region} — rayon ${scan.radius_km} km. Signal Google temporaire : ${scan.places.length} commerces échantillonnés. Priorités : ${top}. Ce signal n’est pas une mesure de part de marché.`,
            next_action:
              radarRows[0]
                ? `Prospecter en priorité : ${radarRows[0].label}.`
                : "Revoir les opportunités territoriales.",
          },
        );

        setSnapshotSaved(
          true,
        );

        await Promise.resolve(
          onRefresh(),
        );
      } catch (caught) {
        setError(
          caught
          instanceof Error
            ? caught.message
            : "demand_radar_snapshot_save_failed",
        );
      } finally {
        setSnapshotSaving(
          false,
        );
      }
    };

  const runForfy =
    async () => {
      if (
        !scan
        || radarRows.length
          === 0
      ) {
        return;
      }

      setForfy({
        loading:
          true,
        saving:
          false,
        saved:
          false,
      });

      try {
        const payload =
          await invokePrpEdge(
            "prp-forfy-agent-run",
            {
              action:
                "run_prp_forfy_agent",
              agent_key:
                "forfy_prp_analyst",
              context: {
                source:
                  "prp_demand_radar_gap_map",
                territory_region:
                  scan.region,
                radius_km:
                  scan.radius_km,
                provider:
                  "google_maps_places",
                market_signal_disclaimer:
                  "Google Nearby est un échantillon de découverte, pas un recensement ni une part de marché.",
                vertical_signals:
                  radarRows.map(
                    (row) => ({
                      vertical:
                        row.label,
                      priority_score:
                        row.priority_score,
                      google_sample_count:
                        row.local_places,
                      untracked_real_candidates:
                        row.untracked_places,
                      active_partners_from_crm:
                        row.active_partners,
                      approved_partners_from_crm:
                        row.approved_partners,
                      crm_pipeline_count:
                        row.pipeline_count,
                      real_candidate_examples:
                        row.candidates
                          .slice(0, 2)
                          .map(
                            (candidate) => ({
                              name:
                                candidate.name,
                              address:
                                candidate.address,
                              primary_type:
                                candidate.primary_type,
                            }),
                          ),
                    }),
                  ),
                instruction:
                  "Analyse uniquement ces signaux fournis. Classe les 3 verticales prioritaires, explique pourquoi, propose une action terrain concrète pour chacune et les risques de surinterprétation. N’invente aucun commerce, aucun chiffre de marché, aucun contact et n’exécute aucune action.",
              },
            },
          );

        if (
          payload.ok
          !== true
        ) {
          throw new Error(
            text(
              payload.error,
              "forfy_demand_radar_failed",
            ),
          );
        }

        setForfy({
          loading:
            false,
          saving:
            false,
          saved:
            false,
          payload,
        });
      } catch (caught) {
        setForfy({
          loading:
            false,
          saving:
            false,
          saved:
            false,
          error:
            caught
            instanceof Error
              ? caught.message
              : "forfy_demand_radar_failed",
        });
      }
    };

  const saveForfy =
    async () => {
      if (
        !scan
        || !forfy?.payload
        || forfy.saving
        || forfy.saved
      ) {
        return;
      }

      const resultId =
        text(
          forfy.payload
            .forfy_result_id,
        );

      if (!resultId) {
        setError(
          "forfy_result_id_missing",
        );
        return;
      }

      setForfy(
        (current) =>
          current
            ? {
                ...current,
                saving:
                  true,
              }
            : current,
      );

      try {
        await invokePrpEdge(
          "prp-workspace-command",
          {
            action:
              "forfy_result_save",
            idempotency_key:
              randomIdempotencyKey(
                "demand-radar-forfy",
              ),
            forfy_result_id:
              resultId,
            agent_key:
              "forfy_prp_analyst",
            business_name:
              `Demand Radar — ${scan.region}`,
            output:
              forfy.payload.output
              || {},
            runtime_meta:
              forfy.payload.runtime_meta
              || {},
            canonical_product_context_version:
              forfy.payload
                .canonical_product_context_version
              || "forfeo-products-v1",
          },
        );

        setForfy(
          (current) =>
            current
              ? {
                  ...current,
                  saving:
                    false,
                  saved:
                    true,
                }
              : current,
        );

        await Promise.resolve(
          onRefresh(),
        );
      } catch (caught) {
        setForfy(
          (current) =>
            current
              ? {
                  ...current,
                  saving:
                    false,
                  error:
                    caught
                    instanceof Error
                      ? caught.message
                      : "forfy_demand_radar_save_failed",
                }
              : current,
        );
      }
    };

  return (
    <section
      className="rounded-3xl border border-cyan-100 bg-gradient-to-br from-white via-white to-cyan-50 p-5 shadow-sm"
      data-lot0f13-demand-radar="true"
    >
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-700">
            0F-13 · Demand Radar + Gap Map
          </p>

          <h3 className="mt-1 text-xl font-black text-slate-950">
            Où Forfeo manque-t-il d’offres sur ton territoire ?
          </h3>

          <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
            Forfeo croise un échantillon réel Google Maps avec ton Pipeline CRM
            pour faire ressortir les verticales sous-couvertes. Le serveur conserve
            toujours la recherche dans ta région administrative PRP.
          </p>
        </div>

        {scan && (
          <div className="rounded-2xl border border-cyan-200 bg-white px-4 py-3 text-right">
            <p className="text-xs font-black uppercase text-cyan-700">
              Région verrouillée
            </p>
            <p className="mt-1 font-black text-slate-950">
              {scan.region}
            </p>
            <p className="text-[11px] font-semibold text-slate-500">
              {scan.radius_km} km · {scan.places.length} résultats échantillonnés
            </p>
          </div>
        )}
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px] lg:items-end">
          <div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-black text-slate-900">
                Rayon du radar
              </p>
              <span className="rounded-full bg-cyan-100 px-3 py-1 text-sm font-black text-cyan-900">
                {radiusKm} km
              </span>
            </div>

            <input
              aria-label="Rayon Demand Radar"
              className="mt-3 w-full accent-cyan-600"
              max={100}
              min={1}
              onChange={(event) => {
                const value = Number(event.target.value);
                setRadiusKm(value);
                setScan(null);
                setForfy(null);
                setSnapshotSaved(false);
              }}
              step={1}
              type="range"
              value={radiusKm}
            />

            <div className="mt-1 flex justify-between text-[10px] font-bold text-slate-400">
              <span>1 km</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span>100 km</span>
            </div>
          </div>

          <button
            className="rounded-xl bg-cyan-700 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
            disabled={scanning || !consent}
            onClick={() => void scanTerritory()}
            type="button"
          >
            {scanning
              ? "Analyse du territoire…"
              : "Analyser mon territoire"}
          </button>
        </div>

        <label className="mt-4 flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-xs font-semibold text-slate-700">
          <input
            checked={consent}
            className="mt-0.5"
            onChange={(event) => {
              setConsent(event.target.checked);
              if (!event.target.checked) {
                setScan(null);
                setForfy(null);
              }
            }}
            type="checkbox"
          />
          <span>
            J’autorise l’utilisation temporaire de ma position uniquement pour ce
            scan. Forfeo ne l’enregistre pas comme position officielle.
          </span>
        </label>

        <p className="mt-3 text-[11px] font-semibold leading-5 text-slate-500">
          Un seul scan multi-vertical est envoyé au moteur Nearby existant.
          À plus de 50 km, sa stratégie multi-zone est réutilisée. Chaque résultat
          hors de la région PRP est éliminé côté serveur.
        </p>
      </div>

      {error && (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-800">
          {error}
        </div>
      )}

      {scan && (
        <>
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-[10px] font-black uppercase text-slate-400">
                Échantillon Google
              </p>
              <p className="mt-1 text-2xl font-black text-slate-950">
                {scan.places.length}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-[10px] font-black uppercase text-slate-400">
                Recherches fournisseur
              </p>
              <p className="mt-1 text-2xl font-black text-slate-950">
                {scan.provider_search_count}
              </p>
              <p className="text-[10px] font-bold text-slate-400">
                {scan.provider_strategy}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-[10px] font-black uppercase text-slate-400">
                Hors territoire retirés
              </p>
              <p className="mt-1 text-2xl font-black text-slate-950">
                {scan.outside_territory_filtered_count}
              </p>
            </div>
          </div>

          <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-700">
                  Carte inversée
                </p>
                <h4 className="text-lg font-black text-slate-950">
                  Ce qui manque dans la zone
                </h4>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  className="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-xs font-black text-cyan-900 disabled:opacity-50"
                  disabled={snapshotSaving || snapshotSaved}
                  onClick={() => void saveSnapshot()}
                  type="button"
                >
                  {snapshotSaving
                    ? "Conservation…"
                    : snapshotSaved
                      ? "✓ Radar conservé dans le CRM"
                      : "Conserver ce radar dans le CRM"}
                </button>

                <button
                  className="rounded-xl bg-violet-700 px-4 py-2 text-xs font-black text-white disabled:opacity-50"
                  disabled={forfy?.loading === true}
                  onClick={() => void runForfy()}
                  type="button"
                >
                  {forfy?.loading
                    ? "Forfy analyse…"
                    : "Forfy · analyser les gaps"}
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
              {radarRows.map((row, index) => (
                <article
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                  key={row.key}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-black text-slate-950">
                        {row.emoji} {row.label}
                      </p>
                      <p className="mt-1 text-[10px] font-black uppercase text-slate-400">
                        Priorité #{index + 1}
                      </p>
                    </div>

                    <span className={`rounded-full px-3 py-1 text-sm font-black ${
                      row.priority_score >= 70
                        ? "bg-rose-100 text-rose-800"
                        : row.priority_score >= 45
                          ? "bg-amber-100 text-amber-800"
                          : "bg-emerald-100 text-emerald-800"
                    }`}>
                      {row.priority_score}/100
                    </span>
                  </div>

                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-cyan-600"
                      style={{
                        width: `${row.priority_score}%`,
                      }}
                    />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                    <div className="rounded-xl bg-white p-2">
                      <p className="text-lg font-black text-slate-900">
                        {row.untracked_places}
                      </p>
                      <p className="text-[9px] font-black uppercase text-slate-400">
                        candidats non suivis
                      </p>
                    </div>
                    <div className="rounded-xl bg-white p-2">
                      <p className="text-lg font-black text-slate-900">
                        {row.active_partners}
                      </p>
                      <p className="text-[9px] font-black uppercase text-slate-400">
                        partenaires actifs CRM
                      </p>
                    </div>
                    <div className="rounded-xl bg-white p-2">
                      <p className="text-lg font-black text-slate-900">
                        {row.pipeline_count}
                      </p>
                      <p className="text-[9px] font-black uppercase text-slate-400">
                        dans le pipeline
                      </p>
                    </div>
                    <div className="rounded-xl bg-white p-2">
                      <p className="text-lg font-black text-slate-900">
                        {row.local_places}
                      </p>
                      <p className="text-[9px] font-black uppercase text-slate-400">
                        signal Google
                      </p>
                    </div>
                  </div>

                  {row.candidates.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-[10px] font-black uppercase text-slate-400">
                        Candidats réels à explorer
                      </p>

                      {row.candidates.map((place) => {
                        const state = saveStates[place.provider_place_id] || "idle";
                        const alreadyInCrm = existingProviderIds.has(place.provider_place_id);

                        return (
                          <div
                            className="rounded-xl border border-slate-200 bg-white p-3"
                            key={place.provider_place_id}
                          >
                            <p className="text-xs font-black text-slate-900">
                              {place.name}
                            </p>
                            <p className="mt-1 line-clamp-2 text-[10px] font-semibold text-slate-500">
                              {place.address || place.primary_type_label}
                            </p>
                            <p className="mt-1 text-[10px] font-bold text-cyan-700">
                              {formatDistance(place.distance_m)} · Google Maps
                            </p>

                            <div className="mt-2 flex flex-wrap gap-2">
                              <a
                                className="rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-black text-slate-600"
                                href={mapsUrl(place)}
                                rel="noreferrer"
                                target="_blank"
                              >
                                Voir dans Maps
                              </a>

                              <button
                                className="rounded-lg bg-slate-900 px-2 py-1 text-[10px] font-black text-white disabled:opacity-40"
                                disabled={
                                  alreadyInCrm
                                  || state === "saving"
                                  || state === "saved"
                                  || state === "duplicate"
                                }
                                onClick={() => void saveCandidate(place)}
                                type="button"
                              >
                                {alreadyInCrm
                                  ? "✓ Déjà dans le CRM"
                                  : state === "saving"
                                    ? "Sauvegarde…"
                                    : state === "saved"
                                      ? "✓ Prospect créé"
                                      : state === "duplicate"
                                        ? "✓ Déjà présent"
                                        : state === "error"
                                          ? "Réessayer"
                                          : "Sauvegarder comme prospect"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </article>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold leading-5 text-amber-900">
              Le score est un signal d’opportunité interne : Google Nearby retourne
              une sélection de commerces, pas un recensement exhaustif. Forfeo ne
              présente donc jamais ce score comme une part de marché ou une demande
              économique mesurée.
            </div>
          </div>

          {forfy?.error && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-xs font-bold text-rose-800">
              {forfy.error}
            </div>
          )}

          {forfy?.payload && (
            <div className="mt-5 rounded-3xl border border-violet-200 bg-violet-50 p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-violet-700">
                    Forfy Analyste PRP
                  </p>
                  <h4 className="mt-1 font-black text-violet-950">
                    Lecture stratégique du Demand Radar
                  </h4>
                </div>

                <button
                  className="rounded-xl border border-violet-200 bg-white px-4 py-2 text-xs font-black text-violet-900 disabled:opacity-50"
                  disabled={forfy.saving || forfy.saved}
                  onClick={() => void saveForfy()}
                  type="button"
                >
                  {forfy.saving
                    ? "Conservation…"
                    : forfy.saved
                      ? "✓ Analyse conservée dans le CRM"
                      : "Conserver l’analyse Forfy"}
                </button>
              </div>

              <pre className="mt-4 max-h-80 overflow-auto whitespace-pre-wrap rounded-2xl bg-slate-950 p-4 text-[11px] font-semibold leading-5 text-slate-100">
                {JSON.stringify(
                  forfy.payload.output || {},
                  null,
                  2,
                )}
              </pre>
            </div>
          )}
        </>
      )}

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 text-[11px] font-semibold leading-5 text-slate-500">
        Garde-fous : position appareil non persistée · données Google temporaires ·
        recherche verrouillée à la région PRP · création d’un prospect uniquement
        après clic humain · Forfy n’invente aucune entreprise et n’exécute aucune
        action contractuelle, financière ou de publication.
      </div>
    </section>
  );
}
